import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { LucideIcon } from "lucide-react";
import { ArrowDownLeft, ArrowUpRight, Check, ChevronDown, ChevronRight, Gauge, Globe, Leaf, Pencil, Plus, Save, Trash2, Zap } from "lucide-react";
import { Fragment, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import {
  deleteParametros,
  getContrapartes,
  getContrato,
  getContratos,
  renombrarContratoFolio,
  saveContrato,
  setCorreoContraparte,
  setProduccionContrato,
  setVigenciaContrato,
  setPlazoPago,
  setTbfinAutomatica,
  saveParametros,
  type ContratoDetalle,
  type ContratoInput,
  type ContratoListItem,
  type NivelTension,
  type ParametroInput,
  type ParametroVersion,
  type VariableDef,
} from "@/lib/api";

const FUENTES = ["constante", "bloomberg", "cpi_us", "tipo_cambio", "treg_anual"];

// Iconos/badge por producto (viene calculado del backend en `productos`).
const PRODUCTO_BADGE: Record<string, { icon: LucideIcon; cls: string }> = {
  Energía: { icon: Zap, cls: "bg-amber-100 text-amber-700" },
  Potencia: { icon: Gauge, cls: "bg-indigo-100 text-indigo-700" },
  CEL: { icon: Leaf, cls: "bg-emerald-100 text-emerald-700" },
  "i-REC": { icon: Globe, cls: "bg-sky-100 text-sky-700" },
};

// Descripción legible del origen/valor de una variable guardada en un snapshot.
const descVar = (v: VariableDef): string => {
  switch (v.fuente) {
    case "constante":
      return `= ${v.valor ?? "—"}`;
    case "bloomberg":
      return `Bloomberg ${v.ticker ?? ""}${v.lag_dias != null ? ` · lag ${v.lag_dias}d` : ""}`.trim();
    case "cpi_us":
      return `CPI EE.UU.${v.mes_offset != null ? ` · m-${v.mes_offset}` : ""}`;
    case "tipo_cambio":
      return `Tipo de cambio${v.lag_dias != null ? ` · lag ${v.lag_dias}d` : ""}`;
    case "treg_anual":
      return "TReg anual";
    default:
      return v.valor != null ? `= ${v.valor}` : (v.fuente ?? "");
  }
};

const fmtFecha = (iso: string | null) =>
  iso
    ? new Date(iso + "T00:00:00").toLocaleDateString("es-MX", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";

type ParamForm = {
  vigente_desde: string;
  variables: VariableDef[];
  formula_strike: string;
  formula_mwh: string;
  notas: string;
};

// Plantilla por defecto = Heat-Rate Call-Option (la fórmula actual de OC001).
const EMPTY_PARAM: ParamForm = {
  vigente_desde: "",
  variables: [
    { nombre: "cvom", fuente: "constante", valor: 4 },
    { nombre: "hr", fuente: "constante", valor: 8 },
    { nombre: "puc_adder", fuente: "constante", valor: 0.6 },
    { nombre: "puc_pct", fuente: "constante", valor: 0.005 },
    { nombre: "disponibilidad", fuente: "constante", valor: 0.98 },
    { nombre: "ee", fuente: "constante", valor: 0 },
    { nombre: "cpi_base", fuente: "constante", valor: 317.671 },
    { nombre: "hsc", fuente: "bloomberg", ticker: "PGFCHOSC Index", lag_dias: 0 },
    { nombre: "cpi", fuente: "cpi_us", mes_offset: 2 },
    { nombre: "tc", fuente: "tipo_cambio", lag_dias: 1 },
    { nombre: "treg", fuente: "treg_anual" },
  ],
  formula_strike: "(cvom*(cpi/cpi_base) + hr*(hsc + puc_adder + puc_pct*hsc)) * tc + treg",
  formula_mwh: "ee*disponibilidad",
  notas: "",
};

function paramToForm(p: ParametroVersion): ParamForm {
  return {
    vigente_desde: "", // nueva versión: fecha en blanco (carry-forward del resto)
    variables: p.variables.map((v) => ({ ...v })),
    formula_strike: p.formula_strike,
    formula_mwh: p.formula_mwh,
    notas: "",
  };
}

const EMPTY_CONTRATO: ContratoInput = {
  folio: "",
  contraparte: "",
  rol_mpp: "vendedor",
  nodo_pml: "06CDU-115",
  central: "",
  modalidad: "Heat-Rate Call-Option",
  fecha_inicio: "",
  fecha_fin: "",
  estado: "activo",
  tipo_precio: "Nodal",
  moneda: "MXN",
  nivel_tension: "lt220",
  id_contraparte: null,
  correo_contraparte: "",
  produccion_inicio: "",
  produccion_fin: "",
};


/** Contraparte de un contrato para el filtro: por id del catálogo si lo tiene,
 *  si no por el nombre capturado en el contrato. */
function contraparteDe(c: ContratoListItem): { clave: string; nombre: string } {
  const nombre = c.contraparte_alias || c.contraparte_nombre || c.contraparte;
  return { clave: c.id_contraparte != null ? `id:${c.id_contraparte}` : `n:${c.contraparte}`, nombre };
}

export function ContratosPage() {
  const qc = useQueryClient();
  // ?id=… (búsqueda global) abre ese contrato al entrar.
  const [searchParams] = useSearchParams();
  const idInicial = Number(searchParams.get("id"));
  const [selectedId, setSelectedId] = useState<number | null>(
    Number.isInteger(idInicial) && idInicial > 0 ? idInicial : null,
  );
  const [showNuevo, setShowNuevo] = useState(false);
  const [filtroProd, setFiltroProd] = useState<string[]>([]);
  // Filtro por rol de MPP: [] = todos; multiselección de 'comprador' | 'vendedor'.
  const [filtroRol, setFiltroRol] = useState<string[]>([]);
  // Filtro por contraparte: "" = todas; si no, la clave de contraparteDe().
  const [filtroCp, setFiltroCp] = useState("");
  const [nuevo, setNuevo] = useState<ContratoInput>(EMPTY_CONTRATO);
  const [pform, setPform] = useState<ParamForm>(EMPTY_PARAM);
  const [msg, setMsg] = useState<string | null>(null);
  const [renombrando, setRenombrando] = useState(false);
  const [folioEdit, setFolioEdit] = useState("");
  const [editCorreo, setEditCorreo] = useState(false);
  const [correoEdit, setCorreoEdit] = useState("");
  const [editProd, setEditProd] = useState(false);
  const [editVig, setEditVig] = useState(false);
  const [vigIni, setVigIni] = useState("");
  const [vigFin, setVigFin] = useState("");
  const [prodIni, setProdIni] = useState("");
  const [prodFin, setProdFin] = useState("");
  const [varsAbiertas, setVarsAbiertas] = useState<Record<number, boolean>>({});

  const listQ = useQuery({ queryKey: ["contratos"], queryFn: getContratos });
  const contrapartesQ = useQuery({ queryKey: ["contrapartes"], queryFn: getContrapartes });
  const detQ = useQuery({
    queryKey: ["contrato", selectedId],
    queryFn: () => getContrato(selectedId as number),
    enabled: selectedId != null,
  });

  // Al cargar el detalle, precargar el form de parámetros con el último snapshot.
  useEffect(() => {
    const d = detQ.data;
    if (!d) return;
    setPform(d.parametros.length ? paramToForm(d.parametros[0]) : EMPTY_PARAM);
  }, [detQ.data]);

  const mContrato = useMutation({
    mutationFn: saveContrato,
    onSuccess: (c) => {
      qc.invalidateQueries({ queryKey: ["contratos"] });
      setShowNuevo(false);
      setNuevo(EMPTY_CONTRATO);
      setSelectedId(c.id_contrato);
    },
  });

  const mRenombrar = useMutation({
    mutationFn: (p: { id: number; folio: string }) => renombrarContratoFolio(p.id, p.folio.trim()),
    onSuccess: (c) => {
      qc.invalidateQueries({ queryKey: ["contratos"] });
      qc.invalidateQueries({ queryKey: ["contrato", c.id_contrato] });
      setRenombrando(false);
      setMsg(`Folio renombrado a ${c.folio}`);
      setTimeout(() => setMsg(null), 3500);
    },
  });

  const mCorreo = useMutation({
    mutationFn: (p: { id: number; correo: string }) => setCorreoContraparte(p.id, p.correo),
    onSuccess: (c) => {
      qc.invalidateQueries({ queryKey: ["contratos"] });
      qc.invalidateQueries({ queryKey: ["contrato", c.id_contrato] });
      setEditCorreo(false);
      setMsg("Correo(s) de la contraparte actualizado(s).");
      setTimeout(() => setMsg(null), 3500);
    },
  });

  const mVig = useMutation({
    mutationFn: (p: { id: number; ini: string; fin: string }) =>
      setVigenciaContrato(p.id, { fecha_inicio: p.ini, fecha_fin: p.fin }),
    onSuccess: (c) => {
      qc.invalidateQueries({ queryKey: ["contratos"] });
      qc.invalidateQueries({ queryKey: ["contrato", c.id_contrato] });
      setEditVig(false);
      setMsg("Vigencia del contrato actualizada.");
      setTimeout(() => setMsg(null), 3500);
    },
  });

  const mProd = useMutation({
    mutationFn: (p: { id: number; ini: string; fin: string }) =>
      setProduccionContrato(p.id, {
        produccion_inicio: p.ini || null,
        produccion_fin: p.fin || null,
      }),
    onSuccess: (c) => {
      qc.invalidateQueries({ queryKey: ["contratos"] });
      qc.invalidateQueries({ queryKey: ["contrato", c.id_contrato] });
      setEditProd(false);
      setMsg("Fechas de producción actualizadas.");
      setTimeout(() => setMsg(null), 3500);
    },
  });

  const mAuto = useMutation({
    mutationFn: (p: { id: number; valor: boolean }) => setTbfinAutomatica(p.id, p.valor),
    onSuccess: (c) => {
      qc.invalidateQueries({ queryKey: ["contratos"] });
      qc.invalidateQueries({ queryKey: ["contrato", c.id_contrato] });
      setMsg(c.tbfin_automatica
        ? "TBFin Automática activada: el calendario la buscará en el estado de cuenta CENACE."
        : "TBFin Automática desactivada.");
      setTimeout(() => setMsg(null), 3500);
    },
  });

  // Plazo de pago de la OC (mig 083): /facturacion lo usa para precalcular la
  // fecha límite de pago de cada factura desde el fin del periodo facturado.
  const [editPlazo, setEditPlazo] = useState(false);
  const [plazoDias, setPlazoDias] = useState("");
  const [plazoHabiles, setPlazoHabiles] = useState(true);
  const mPlazo = useMutation({
    mutationFn: (p: { id: number; dias: string; habiles: boolean }) =>
      setPlazoPago(p.id, {
        plazo_pago_dias: p.dias === "" ? null : Number(p.dias),
        plazo_pago_habiles: p.habiles,
      }),
    onSuccess: (c) => {
      qc.invalidateQueries({ queryKey: ["contratos"] });
      qc.invalidateQueries({ queryKey: ["contrato", c.id_contrato] });
      setEditPlazo(false);
      setMsg(
        c.plazo_pago_dias == null
          ? "Plazo de pago borrado: la fecha límite se capturará factura por factura."
          : `Plazo de pago: ${c.plazo_pago_dias} ${
              c.plazo_pago_habiles ? "Días Hábiles" : "Días naturales"
            } tras la factura.`,
      );
      setTimeout(() => setMsg(null), 3500);
    },
  });

  const mParam = useMutation({
    mutationFn: (p: { id: number; payload: ParametroInput }) =>
      saveParametros(p.id, p.payload),
    onSuccess: (d: ContratoDetalle) => {
      qc.invalidateQueries({ queryKey: ["contrato", d.id_contrato] });
      qc.invalidateQueries({ queryKey: ["contratos"] });
      setMsg(`Versión guardada (vigente desde ${pform.vigente_desde})`);
      setTimeout(() => setMsg(null), 3500);
    },
  });

  const mDel = useMutation({
    mutationFn: (p: { id: number; idParam: number }) =>
      deleteParametros(p.id, p.idParam),
    onSuccess: (d: ContratoDetalle) => {
      qc.invalidateQueries({ queryKey: ["contrato", d.id_contrato] });
      qc.invalidateQueries({ queryKey: ["contratos"] });
    },
  });

  const contratos = listQ.data?.items ?? [];
  const contratosVisibles = contratos
    .filter((c) =>
      filtroProd.length === 0 || (c.productos ?? []).some((p) => filtroProd.includes(p)))
    .filter((c) => filtroRol.length === 0 || filtroRol.includes(c.rol_mpp ?? ""))
    .filter((c) => !filtroCp || contraparteDe(c).clave === filtroCp);
  // Opciones del filtro de contraparte (con # de contratos), ordenadas por nombre.
  const opcionesCp = [
    ...contratos
      .reduce((m, c) => {
        const { clave, nombre } = contraparteDe(c);
        const o = m.get(clave) ?? { clave, nombre, n: 0 };
        o.n += 1;
        return m.set(clave, o);
      }, new Map<string, { clave: string; nombre: string; n: number }>())
      .values(),
  ].sort((a, b) => a.nombre.localeCompare(b.nombre, "es"));

  // Auto-seleccionar el primer contrato si no hay uno elegido.
  useEffect(() => {
    if (selectedId == null && contratos.length > 0) {
      setSelectedId(contratos[0].id_contrato);
    }
  }, [contratos, selectedId]);

  // Salir de los modos de edición inline al cambiar de contrato.
  useEffect(() => {
    setRenombrando(false);
    setEditCorreo(false);
  }, [selectedId]);

  const setVar = (i: number, patch: Partial<VariableDef>) => {
    setPform((f) => ({
      ...f,
      variables: f.variables.map((v, idx) => (idx === i ? { ...v, ...patch } : v)),
    }));
  };
  const addVar = () =>
    setPform((f) => ({ ...f, variables: [...f.variables, { nombre: "", fuente: "constante", valor: 0 }] }));
  const delVar = (i: number) =>
    setPform((f) => ({ ...f, variables: f.variables.filter((_, idx) => idx !== i) }));

  const handleSaveParam = () => {
    if (selectedId == null) return;
    if (!pform.vigente_desde) {
      setMsg("Indica la fecha 'vigente desde'.");
      return;
    }
    const payload: ParametroInput = {
      vigente_desde: pform.vigente_desde,
      variables: pform.variables
        .filter((v) => v.nombre.trim())
        .map((v) => ({
          nombre: v.nombre.trim(),
          fuente: v.fuente,
          valor: v.fuente === "constante" ? Number(v.valor ?? 0) : null,
          ticker: v.fuente === "bloomberg" ? v.ticker ?? null : null,
          lag_dias:
            v.fuente === "bloomberg" || v.fuente === "tipo_cambio"
              ? Number(v.lag_dias ?? 0)
              : null,
          mes_offset: v.fuente === "cpi_us" ? Number(v.mes_offset ?? 0) : null,
        })),
      formula_strike: pform.formula_strike.trim(),
      formula_mwh: pform.formula_mwh.trim(),
      notas: pform.notas || null,
    };
    mParam.mutate({ id: selectedId, payload });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">Contratos TBFin</h1>
        <p className="text-sm text-slate-500 mt-1">
          Parámetros por contrato, versionados por fecha de vigencia. El cálculo TBFin usa el
          snapshot vigente para cada fecha de operación.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6">
        {/* ---- Lista de contratos ---- */}
        <div className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-sm font-semibold text-slate-700">Contratos</h2>
            <div className="flex flex-wrap items-center gap-1">
              {(["Energía", "Potencia", "CEL"] as const).map((p) => {
                const b = PRODUCTO_BADGE[p];
                const Icon = b.icon;
                const active = filtroProd.includes(p);
                return (
                  <button
                    key={p}
                    type="button"
                    title={`Filtrar por ${p}`}
                    aria-pressed={active}
                    onClick={() =>
                      setFiltroProd((f) =>
                        f.includes(p) ? f.filter((x) => x !== p) : [...f, p],
                      )
                    }
                    className={`inline-flex items-center gap-0.5 rounded px-1.5 py-1 text-[10px] font-semibold border transition ${
                      active
                        ? `${b.cls} border-transparent`
                        : "bg-white text-slate-400 border-slate-200 hover:bg-slate-50"
                    }`}
                  >
                    <Icon className="w-3 h-3" /> {p}
                  </button>
                );
              })}
              {/* Filtro por rol de MPP (multiselección, igual que el de producto). */}
              {([["comprador", "Compra"], ["vendedor", "Venta"]] as const).map(([rol, label]) => {
                const active = filtroRol.includes(rol);
                const cls = rol === "comprador"
                  ? "bg-indigo-100 text-indigo-700"
                  : "bg-amber-100 text-amber-700";
                return (
                  <button
                    key={rol}
                    type="button"
                    title={`Filtrar por ${label}`}
                    aria-pressed={active}
                    onClick={() =>
                      setFiltroRol((f) =>
                        f.includes(rol) ? f.filter((x) => x !== rol) : [...f, rol],
                      )
                    }
                    className={`inline-flex items-center gap-0.5 rounded px-1.5 py-1 text-[10px] font-semibold border transition ${
                      active
                        ? `${cls} border-transparent`
                        : "bg-white text-slate-400 border-slate-200 hover:bg-slate-50"
                    }`}
                  >
                    {rol === "comprador" ? <ArrowDownLeft className="w-3 h-3" /> : <ArrowUpRight className="w-3 h-3" />}
                    {label}
                  </button>
                );
              })}
              <button
                type="button"
                onClick={() => setShowNuevo((v) => !v)}
                className="inline-flex items-center gap-1 text-xs border rounded px-2 py-1 hover:bg-slate-50"
              >
                <Plus className="w-3 h-3" /> Nuevo
              </button>
            </div>
          </div>

          <select
            value={filtroCp}
            onChange={(e) => setFiltroCp(e.target.value)}
            title="Filtrar por contraparte"
            className={`w-full border rounded px-2 py-1 text-xs ${filtroCp ? "border-blue-300 bg-blue-50 text-blue-800" : "text-slate-600"}`}
          >
            <option value="">Todas las contrapartes ({contratos.length})</option>
            {opcionesCp.map((o) => (
              <option key={o.clave} value={o.clave}>
                {o.nombre} ({o.n})
              </option>
            ))}
          </select>

          {showNuevo && (
            <div className="bg-white border rounded-lg p-3 space-y-2">
              <Input label="Folio" value={nuevo.folio} onChange={(v) => setNuevo({ ...nuevo, folio: v })} placeholder="001MPP/GIN" />
              <label className="block">
                <span className="text-xs font-medium text-slate-600">Rol de MPP</span>
                <select
                  className="mt-1 border rounded px-2 py-1.5 w-full text-sm"
                  value={nuevo.rol_mpp}
                  onChange={(e) => setNuevo({ ...nuevo, rol_mpp: e.target.value })}
                >
                  <option value="vendedor">Vendedor (emite TBFin)</option>
                  <option value="comprador">Comprador (valida)</option>
                </select>
              </label>
              <label className="block">
                <span className="text-xs font-medium text-slate-600">Contraparte (del catálogo)</span>
                <select
                  className="mt-1 border rounded px-2 py-1.5 w-full text-sm"
                  value={nuevo.id_contraparte ?? ""}
                  onChange={(e) => {
                    const id = e.target.value ? Number(e.target.value) : null;
                    const cp = (contrapartesQ.data ?? []).find((x) => x.id_contraparte === id);
                    setNuevo({ ...nuevo, id_contraparte: id, contraparte: cp?.nombre ?? "" });
                  }}
                >
                  <option value="">— selecciona —</option>
                  {(contrapartesQ.data ?? []).map((cp) => (
                    <option key={cp.id_contraparte} value={cp.id_contraparte}>
                      {cp.nombre}{cp.id_cuenta_sim ? ` (${cp.id_cuenta_sim})` : ""}
                    </option>
                  ))}
                </select>
                <span className="block text-[10px] text-slate-400 mt-0.5">
                  ¿Falta? agrégala en la pestaña Contrapartes.
                </span>
              </label>
              <Input
                label="Correo(s) de la contraparte"
                value={nuevo.correo_contraparte ?? ""}
                onChange={(v) => setNuevo({ ...nuevo, correo_contraparte: v })}
                placeholder="ops@cp.com, tesoreria@cp.com"
              />
              <Input label="Central" value={nuevo.central ?? ""} onChange={(v) => setNuevo({ ...nuevo, central: v })} />
              <label className="block">
                <span className="text-xs font-medium text-slate-600">Tipo de Nodo</span>
                <select
                  className="mt-1 border rounded px-2 py-1.5 w-full text-sm"
                  value={nuevo.tipo_precio ?? "Nodal"}
                  onChange={(e) => setNuevo({ ...nuevo, tipo_precio: e.target.value })}
                >
                  <option value="Nodal">Nodal</option>
                  <option value="Zonal">Zonal</option>
                </select>
              </label>
              <label className="block">
                <span className="text-xs font-medium text-slate-600">Moneda de liquidación</span>
                <select
                  className="mt-1 border rounded px-2 py-1.5 w-full text-sm"
                  value={nuevo.moneda ?? "MXN"}
                  onChange={(e) => setNuevo({ ...nuevo, moneda: e.target.value })}
                >
                  <option value="MXN">MXN (canónica ×TC + TReg)</option>
                  <option value="USD">USD (energía gas + TReg/TC último día hábil)</option>
                </select>
              </label>
              <label className="block">
                <span className="text-xs font-medium text-slate-600">
                  Nivel de tensión (cláusula de TReg)
                </span>
                <select
                  className="mt-1 border rounded px-2 py-1.5 w-full text-sm"
                  value={nuevo.nivel_tension ?? "lt220"}
                  onChange={(e) =>
                    setNuevo({ ...nuevo, nivel_tension: e.target.value as NivelTension })
                  }
                >
                  <option value="lt220">&lt; 220 kV</option>
                  <option value="ge220">&gt;= 220 kV</option>
                </select>
                <span className="text-[11px] text-slate-400">
                  Lo dice la definición de Treg de la OC; elige qué tarifa de transmisión aplica.
                </span>
              </label>
              <Input label="Nodo" value={nuevo.nodo_pml} onChange={(v) => setNuevo({ ...nuevo, nodo_pml: v })} placeholder="06CDU-115" />
              <div className="grid grid-cols-2 gap-2">
                <Input label="Vigencia desde" type="date" value={nuevo.fecha_inicio ?? ""} onChange={(v) => setNuevo({ ...nuevo, fecha_inicio: v })} />
                <Input label="Vigencia hasta" type="date" value={nuevo.fecha_fin ?? ""} onChange={(v) => setNuevo({ ...nuevo, fecha_fin: v })} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Input label="Inicio producción" type="date" value={nuevo.produccion_inicio ?? ""} onChange={(v) => setNuevo({ ...nuevo, produccion_inicio: v })} />
                <Input label="Fin producción" type="date" value={nuevo.produccion_fin ?? ""} onChange={(v) => setNuevo({ ...nuevo, produccion_fin: v })} />
              </div>
              <p className="text-[11px] text-slate-400">
                Producción = cuándo la potencia está realmente disponible; la usa el reporte de inventario.
              </p>
              <button
                type="button"
                onClick={() => mContrato.mutate(nuevo)}
                disabled={!nuevo.folio || !nuevo.id_contraparte || mContrato.isPending}
                className="w-full inline-flex items-center justify-center gap-1 bg-blue-600 text-white rounded px-3 py-1.5 text-sm hover:bg-blue-700 disabled:opacity-50"
              >
                <Save className="w-4 h-4" /> Guardar contrato
              </button>
              {mContrato.isError && (
                <div className="text-xs text-red-600">{(mContrato.error as Error).message}</div>
              )}
            </div>
          )}

          <div className="bg-white border rounded-lg divide-y">
            {listQ.isLoading && <div className="p-4 text-sm text-slate-500">Cargando…</div>}
            {contratosVisibles.length === 0 && !listQ.isLoading && (
              <div className="p-4 text-sm text-slate-500">
                {contratos.length === 0
                  ? "Sin contratos."
                  : "Ningún contrato coincide con el filtro."}
              </div>
            )}
            {contratosVisibles.map((c) => (
              <button
                key={c.id_contrato}
                type="button"
                onClick={() => setSelectedId(c.id_contrato)}
                className={`w-full text-left px-3 py-2 hover:bg-slate-50 ${
                  selectedId === c.id_contrato ? "bg-blue-50" : ""
                }`}
              >
                <div className="font-medium text-sm text-slate-800">{c.folio}</div>
                <div className="text-xs text-slate-500">{c.contraparte_alias || c.contraparte}</div>
                <div className="text-[11px] text-slate-400">
                  {c.rol_mpp} · {c.nodo_pml} · {c.n_versiones} versión(es)
                </div>
                {c.productos && c.productos.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {c.productos.map((p) => {
                      const b = PRODUCTO_BADGE[p];
                      const Icon = b?.icon;
                      return (
                        <span
                          key={p}
                          className={`inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[10px] font-semibold ${
                            b?.cls ?? "bg-slate-100 text-slate-600"
                          }`}
                        >
                          {Icon && <Icon className="w-3 h-3" />}
                          {p}
                        </span>
                      );
                    })}
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* ---- Detalle del contrato ---- */}
        <div className="space-y-4">
          {selectedId == null && (
            <div className="bg-white border rounded-lg p-8 text-center text-sm text-slate-500">
              Selecciona un contrato para ver y capturar sus parámetros.
            </div>
          )}

          {selectedId != null && detQ.data && (
            <>
              <div className="bg-white border rounded-lg p-4">
                <div className="flex items-baseline justify-between gap-2">
                  {renombrando ? (
                    <div className="flex items-center gap-2 flex-1">
                      <input
                        autoFocus
                        type="text"
                        value={folioEdit}
                        onChange={(e) => setFolioEdit(e.target.value)}
                        placeholder="001MPP/GIN"
                        className="border rounded px-2 py-1 text-sm font-semibold w-56"
                      />
                      <button
                        type="button"
                        onClick={() =>
                          mRenombrar.mutate({ id: detQ.data!.id_contrato, folio: folioEdit })
                        }
                        disabled={
                          !folioEdit.trim() ||
                          folioEdit.trim() === detQ.data.folio ||
                          mRenombrar.isPending
                        }
                        className="inline-flex items-center gap-1 bg-blue-600 text-white rounded px-2.5 py-1 text-xs hover:bg-blue-700 disabled:opacity-50"
                      >
                        <Check className="w-3.5 h-3.5" /> {mRenombrar.isPending ? "Guardando…" : "Guardar"}
                      </button>
                      <button
                        type="button"
                        onClick={() => setRenombrando(false)}
                        className="inline-flex items-center border rounded px-2.5 py-1 text-xs hover:bg-slate-50"
                      >
                        Cancelar
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <h2 className="text-lg font-semibold">{detQ.data.folio}</h2>
                      <button
                        type="button"
                        onClick={() => {
                          setFolioEdit(detQ.data!.folio);
                          setRenombrando(true);
                        }}
                        title="Renombrar el folio del contrato"
                        className="inline-flex items-center gap-1 text-xs text-slate-500 border rounded px-2 py-0.5 hover:bg-slate-50"
                      >
                        <Pencil className="w-3 h-3" /> Renombrar
                      </button>
                    </div>
                  )}
                  <span className="text-xs text-slate-400">{detQ.data.estado}</span>
                </div>
                {mRenombrar.isError && (
                  <div className="text-xs text-red-600 mt-1">
                    {(mRenombrar.error as Error).message}
                  </div>
                )}
                <div className="text-sm text-slate-600">{detQ.data.contraparte_alias || detQ.data.contraparte}</div>
                <div className="text-xs text-slate-400 mt-1">
                  {detQ.data.rol_mpp} · nodo {detQ.data.nodo_pml}
                  {detQ.data.central ? ` · ${detQ.data.central}` : ""} · {detQ.data.modalidad}
                  {" · "}<span className="font-medium">{detQ.data.moneda}</span>
                </div>
                <div className="text-xs text-slate-400">
                  Tipo de Nodo {detQ.data.tipo_precio ?? "—"}
                  {" · TReg "}
                  <span className="font-medium">
                    {detQ.data.nivel_tension === "ge220" ? ">= 220 kV" : "< 220 kV"}
                  </span>
                  {" · contraparte "}{detQ.data.contraparte_nombre ?? "—"}
                  {detQ.data.contraparte_cuenta_sim ? ` (${detQ.data.contraparte_cuenta_sim}/${detQ.data.contraparte_cuenta_orden ?? "—"})` : ""}
                </div>
                {/* Vigencia comercial — editable (ampliar/recortar la OC sin tocar parámetros) */}
                <div className="text-xs text-slate-500 mt-1">
                  {editVig ? (
                    <div className="flex flex-wrap items-center gap-2">
                      <span>Vigencia</span>
                      <label>
                        desde
                        <input
                          type="date"
                          value={vigIni}
                          onChange={(e) => setVigIni(e.target.value)}
                          className="ml-1 border rounded px-2 py-1 text-sm"
                        />
                      </label>
                      <label>
                        hasta
                        <input
                          type="date"
                          value={vigFin}
                          onChange={(e) => setVigFin(e.target.value)}
                          className="ml-1 border rounded px-2 py-1 text-sm"
                        />
                      </label>
                      <button
                        type="button"
                        onClick={() => mVig.mutate({ id: detQ.data!.id_contrato, ini: vigIni, fin: vigFin })}
                        disabled={mVig.isPending || !vigIni || !vigFin}
                        className="inline-flex items-center gap-1 bg-blue-600 text-white rounded px-2.5 py-1 text-xs hover:bg-blue-700 disabled:opacity-50"
                      >
                        <Check className="w-3.5 h-3.5" /> {mVig.isPending ? "Guardando…" : "Guardar"}
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditVig(false)}
                        className="inline-flex items-center border rounded px-2.5 py-1 text-xs hover:bg-slate-50"
                      >
                        Cancelar
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span>
                        Vigencia: <span className="font-medium">{fmtFecha(detQ.data.fecha_inicio)}</span> a{" "}
                        <span className="font-medium">{fmtFecha(detQ.data.fecha_fin)}</span>
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          setVigIni(detQ.data!.fecha_inicio ?? "");
                          setVigFin(detQ.data!.fecha_fin ?? "");
                          setEditVig(true);
                        }}
                        title="Editar la vigencia comercial (p. ej. ampliar la OC). No modifica los parámetros versionados."
                        className="inline-flex items-center gap-1 text-slate-500 border rounded px-2 py-0.5 hover:bg-slate-50"
                      >
                        <Pencil className="w-3 h-3" /> Editar
                      </button>
                    </div>
                  )}
                  {mVig.isError && (
                    <div className="text-xs text-red-600 mt-1">{(mVig.error as Error).message}</div>
                  )}
                </div>

                {/* Fechas de producción — disponibilidad real (reporte de inventario) */}
                <div className="mt-2 pt-2 border-t">
                  {editProd ? (
                    <div className="flex flex-wrap items-center gap-2">
                      <label className="text-xs text-slate-500">
                        Inicio
                        <input
                          type="date"
                          value={prodIni}
                          onChange={(e) => setProdIni(e.target.value)}
                          className="ml-1 border rounded px-2 py-1 text-sm"
                        />
                      </label>
                      <label className="text-xs text-slate-500">
                        Fin
                        <input
                          type="date"
                          value={prodFin}
                          onChange={(e) => setProdFin(e.target.value)}
                          className="ml-1 border rounded px-2 py-1 text-sm"
                        />
                      </label>
                      <button
                        type="button"
                        onClick={() =>
                          mProd.mutate({ id: detQ.data!.id_contrato, ini: prodIni, fin: prodFin })
                        }
                        disabled={mProd.isPending}
                        className="inline-flex items-center gap-1 bg-blue-600 text-white rounded px-2.5 py-1 text-xs hover:bg-blue-700 disabled:opacity-50"
                      >
                        <Check className="w-3.5 h-3.5" /> {mProd.isPending ? "Guardando…" : "Guardar"}
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditProd(false)}
                        className="inline-flex items-center border rounded px-2.5 py-1 text-xs hover:bg-slate-50"
                      >
                        Cancelar
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <span>
                        Producción (disponibilidad real):{" "}
                        <span className="font-medium text-slate-700">
                          {detQ.data.produccion_inicio || detQ.data.produccion_fin
                            ? `${fmtFecha(detQ.data.produccion_inicio)} a ${fmtFecha(detQ.data.produccion_fin)}`
                            : "— sin configurar"}
                        </span>
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          setProdIni(detQ.data!.produccion_inicio ?? "");
                          setProdFin(detQ.data!.produccion_fin ?? "");
                          setEditProd(true);
                        }}
                        title="Editar fechas de producción (usadas por el reporte de inventario)"
                        className="inline-flex items-center gap-1 text-slate-500 border rounded px-2 py-0.5 hover:bg-slate-50"
                      >
                        <Pencil className="w-3 h-3" /> Editar
                      </button>
                    </div>
                  )}
                  {mProd.isError && (
                    <div className="text-xs text-red-600 mt-1">{(mProd.error as Error).message}</div>
                  )}
                </div>

                {/* TBFin Automática — la registra la contraparte, CENACE la formaliza sola */}
                <div className="mt-2 pt-2 border-t flex items-center gap-2 text-xs text-slate-500">
                  <label className="inline-flex items-center gap-1.5 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={detQ.data.tbfin_automatica}
                      disabled={mAuto.isPending}
                      onChange={(e) =>
                        mAuto.mutate({ id: detQ.data!.id_contrato, valor: e.target.checked })
                      }
                      className="w-3.5 h-3.5 accent-purple-600 cursor-pointer"
                    />
                    <span className="font-medium text-slate-700">TBFin Automática</span>
                  </label>
                  <span>
                    — la registra la contraparte y CENACE la formaliza sola; el calendario
                    marca <span className="font-semibold text-purple-700">U</span> los días ya
                    formalizados (estado de cuenta).
                  </span>
                  {mAuto.isError && (
                    <span className="text-red-600">{(mAuto.error as Error).message}</span>
                  )}
                </div>

                {/* Correo(s) de la contraparte — CC del acuse de la TBFin */}
                <div className="mt-2 pt-2 border-t">
                  {editCorreo ? (
                    <div className="flex items-center gap-2">
                      <input
                        autoFocus
                        type="text"
                        value={correoEdit}
                        onChange={(e) => setCorreoEdit(e.target.value)}
                        placeholder="ops@cp.com, tesoreria@cp.com"
                        className="border rounded px-2 py-1 text-sm flex-1"
                      />
                      <button
                        type="button"
                        onClick={() =>
                          mCorreo.mutate({ id: detQ.data!.id_contrato, correo: correoEdit })
                        }
                        disabled={mCorreo.isPending}
                        className="inline-flex items-center gap-1 bg-blue-600 text-white rounded px-2.5 py-1 text-xs hover:bg-blue-700 disabled:opacity-50"
                      >
                        <Check className="w-3.5 h-3.5" /> {mCorreo.isPending ? "Guardando…" : "Guardar"}
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditCorreo(false)}
                        className="inline-flex items-center border rounded px-2.5 py-1 text-xs hover:bg-slate-50"
                      >
                        Cancelar
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <span>
                        Correo(s) contraparte (CC del acuse):{" "}
                        <span className="font-medium text-slate-700">
                          {detQ.data.correo_contraparte || "— sin configurar"}
                        </span>
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          setCorreoEdit(detQ.data!.correo_contraparte ?? "");
                          setEditCorreo(true);
                        }}
                        title="Editar correo(s) de la contraparte"
                        className="inline-flex items-center gap-1 text-slate-500 border rounded px-2 py-0.5 hover:bg-slate-50"
                      >
                        <Pencil className="w-3 h-3" /> Editar
                      </button>
                    </div>
                  )}
                  {mCorreo.isError && (
                    <div className="text-xs text-red-600 mt-1">{(mCorreo.error as Error).message}</div>
                  )}
                </div>

                {/* Plazo de pago de la OC — precalcula la fecha límite en /facturacion */}
                <div className="mt-2 pt-2 border-t">
                  {editPlazo ? (
                    <div className="flex flex-wrap items-center gap-2">
                      <input
                        autoFocus
                        type="number"
                        min="0"
                        max="365"
                        value={plazoDias}
                        onChange={(e) => setPlazoDias(e.target.value)}
                        placeholder="días"
                        className="border rounded px-2 py-1 text-sm w-24"
                      />
                      <select
                        value={plazoHabiles ? "habiles" : "naturales"}
                        onChange={(e) => setPlazoHabiles(e.target.value === "habiles")}
                        className="border rounded px-2 py-1 text-sm"
                      >
                        <option value="habiles">Días Hábiles</option>
                        <option value="naturales">Días naturales</option>
                      </select>
                      <span className="text-xs text-slate-500">
                        tras la factura (vacío = la OC pactó fechas fijas)
                      </span>
                      <button
                        type="button"
                        onClick={() =>
                          mPlazo.mutate({
                            id: detQ.data!.id_contrato,
                            dias: plazoDias,
                            habiles: plazoHabiles,
                          })
                        }
                        disabled={mPlazo.isPending}
                        className="inline-flex items-center gap-1 bg-blue-600 text-white rounded px-2.5 py-1 text-xs hover:bg-blue-700 disabled:opacity-50"
                      >
                        <Check className="w-3.5 h-3.5" /> {mPlazo.isPending ? "Guardando…" : "Guardar"}
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditPlazo(false)}
                        className="inline-flex items-center border rounded px-2.5 py-1 text-xs hover:bg-slate-50"
                      >
                        Cancelar
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <span>
                        Plazo de pago (de la OC):{" "}
                        <span className="font-medium text-slate-700">
                          {detQ.data.plazo_pago_dias == null
                            ? "— sin plazo (fechas fijas en la OC)"
                            : `${detQ.data.plazo_pago_dias} ${
                                detQ.data.plazo_pago_habiles ? "Días Hábiles" : "Días naturales"
                              } tras la factura`}
                        </span>
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          setPlazoDias(
                            detQ.data!.plazo_pago_dias == null
                              ? ""
                              : String(detQ.data!.plazo_pago_dias),
                          );
                          setPlazoHabiles(detQ.data!.plazo_pago_habiles ?? true);
                          setEditPlazo(true);
                        }}
                        title="Editar el plazo de pago (precalcula la fecha límite en Cálculo de facturación)"
                        className="inline-flex items-center gap-1 text-slate-500 border rounded px-2 py-0.5 hover:bg-slate-50"
                      >
                        <Pencil className="w-3 h-3" /> Editar
                      </button>
                    </div>
                  )}
                  {mPlazo.isError && (
                    <div className="text-xs text-red-600 mt-1">{(mPlazo.error as Error).message}</div>
                  )}
                </div>
              </div>

              {/* Form de nueva versión: variables + fórmulas (carry-forward del último) */}
              <div className="bg-white border rounded-lg p-4 space-y-4">
                <h3 className="text-sm font-semibold text-slate-800">
                  Nueva versión — fórmula y variables
                  <span className="font-normal text-slate-400"> — precargada del snapshot más reciente</span>
                </h3>
                <div className="max-w-xs">
                  <Input
                    label="Vigente desde"
                    type="date"
                    value={pform.vigente_desde}
                    onChange={(v) => setPform({ ...pform, vigente_desde: v })}
                  />
                </div>

                {/* Variables */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-semibold text-slate-700">Variables</span>
                    <button type="button" onClick={addVar}
                      className="inline-flex items-center gap-1 text-xs border rounded px-2 py-1 hover:bg-slate-50">
                      <Plus className="w-3 h-3" /> Variable
                    </button>
                  </div>
                  <div className="space-y-1.5">
                    {pform.variables.map((v, i) => (
                      <div key={i} className="flex items-center gap-1.5">
                        <input
                          className="border rounded px-2 py-1 text-xs font-mono w-28"
                          placeholder="nombre"
                          value={v.nombre}
                          onChange={(e) => setVar(i, { nombre: e.target.value })}
                        />
                        <select
                          className="border rounded px-1 py-1 text-xs w-28"
                          value={v.fuente}
                          onChange={(e) => setVar(i, { fuente: e.target.value })}
                        >
                          {FUENTES.map((f) => <option key={f} value={f}>{f}</option>)}
                        </select>
                        {v.fuente === "constante" && (
                          <input type="number" step="any" className="border rounded px-2 py-1 text-xs w-28 tabular-nums"
                            placeholder="valor" value={v.valor ?? ""} onChange={(e) => setVar(i, { valor: Number(e.target.value) })} />
                        )}
                        {v.fuente === "bloomberg" && (
                          <>
                            <input className="border rounded px-2 py-1 text-xs w-40" placeholder="ticker"
                              value={v.ticker ?? ""} onChange={(e) => setVar(i, { ticker: e.target.value })} />
                            <input type="number" className="border rounded px-2 py-1 text-xs w-16 tabular-nums" placeholder="lag"
                              value={v.lag_dias ?? 0} onChange={(e) => setVar(i, { lag_dias: Number(e.target.value) })} title="lag días" />
                          </>
                        )}
                        {v.fuente === "cpi_us" && (
                          <input type="number" className="border rounded px-2 py-1 text-xs w-20 tabular-nums" placeholder="m-offset"
                            value={v.mes_offset ?? 0} onChange={(e) => setVar(i, { mes_offset: Number(e.target.value) })} title="meses de offset (2 = m-2)" />
                        )}
                        {v.fuente === "tipo_cambio" && (
                          <input type="number" className="border rounded px-2 py-1 text-xs w-16 tabular-nums" placeholder="lag"
                            value={v.lag_dias ?? 1} onChange={(e) => setVar(i, { lag_dias: Number(e.target.value) })} title="lag días" />
                        )}
                        <button type="button" onClick={() => delVar(i)} className="text-slate-300 hover:text-red-600 ml-auto">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Fórmulas */}
                <label className="block">
                  <span className="text-xs font-medium text-slate-600">Fórmula del strike (umbral)</span>
                  <textarea rows={2} className="mt-1 border rounded px-2 py-1.5 w-full text-xs font-mono"
                    value={pform.formula_strike} onChange={(e) => setPform({ ...pform, formula_strike: e.target.value })} />
                </label>
                <label className="block">
                  <span className="text-xs font-medium text-slate-600">Fórmula de MWh (por hora activa)</span>
                  <span className="block text-[10px] text-slate-400">dispone también de <code>pml</code> y <code>strike</code></span>
                  <textarea rows={1} className="mt-1 border rounded px-2 py-1.5 w-full text-xs font-mono"
                    value={pform.formula_mwh} onChange={(e) => setPform({ ...pform, formula_mwh: e.target.value })} />
                </label>

                <Input label="Notas" value={pform.notas} onChange={(v) => setPform({ ...pform, notas: v })} />
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={handleSaveParam}
                    disabled={mParam.isPending}
                    className="inline-flex items-center gap-2 bg-blue-600 text-white rounded px-4 py-2 text-sm hover:bg-blue-700 disabled:opacity-50"
                  >
                    <Save className="w-4 h-4" /> Guardar versión
                  </button>
                  {msg && <span className="text-sm text-emerald-600">{msg}</span>}
                  {mParam.isError && (
                    <span className="text-sm text-red-600">{(mParam.error as Error).message}</span>
                  )}
                </div>
              </div>

              {/* Histórico de versiones */}
              <div className="bg-white border rounded-lg overflow-hidden">
                <div className="px-4 py-3 border-b">
                  <h3 className="text-sm font-semibold text-slate-800">Versiones (snapshots)</h3>
                </div>
                <table className="w-full text-sm">
                  <thead className="bg-slate-100 text-slate-600 text-left">
                    <tr>
                      <th className="px-3 py-2 font-medium">Vigente desde</th>
                      <th className="px-3 py-2 font-medium">Fórmula del strike</th>
                      <th className="px-3 py-2 font-medium text-right"># vars</th>
                      <th className="px-3 py-2 font-medium"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {detQ.data.parametros.length === 0 && (
                      <tr>
                        <td colSpan={4} className="px-3 py-6 text-center text-slate-500">
                          Sin versiones. Captura la primera arriba.
                        </td>
                      </tr>
                    )}
                    {detQ.data.parametros.map((p) => {
                      const abierto = !!varsAbiertas[p.id];
                      return (
                        <Fragment key={p.id}>
                          <tr className="border-t hover:bg-slate-50">
                            <td className="px-3 py-2 font-medium tabular-nums whitespace-nowrap">{p.vigente_desde}</td>
                            <td className="px-3 py-2 font-mono text-[11px] text-slate-600">{p.formula_strike}</td>
                            <td className="px-3 py-2 text-right tabular-nums">
                              <button
                                type="button"
                                onClick={() =>
                                  setVarsAbiertas((s) => ({ ...s, [p.id]: !s[p.id] }))
                                }
                                title="Ver variables guardadas"
                                className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline"
                              >
                                {abierto ? (
                                  <ChevronDown className="w-3.5 h-3.5" />
                                ) : (
                                  <ChevronRight className="w-3.5 h-3.5" />
                                )}
                                {p.variables.length}
                              </button>
                            </td>
                            <td className="px-3 py-2 text-right">
                              <button
                                type="button"
                                onClick={() => mDel.mutate({ id: selectedId, idParam: p.id })}
                                className="text-slate-400 hover:text-red-600"
                                title="Eliminar versión"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                          {abierto && (
                            <tr className="bg-slate-50/60">
                              <td colSpan={4} className="px-4 py-3">
                                {p.variables.length === 0 ? (
                                  <div className="text-xs text-slate-500">Este snapshot no tiene variables.</div>
                                ) : (
                                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-1">
                                    {p.variables.map((v, i) => (
                                      <div
                                        key={`${v.nombre}-${i}`}
                                        className="flex items-baseline justify-between gap-2 border-b border-slate-200/70 py-0.5"
                                      >
                                        <span className="font-mono text-[11px] font-semibold text-slate-700">
                                          {v.nombre}
                                        </span>
                                        <span className="text-[11px] text-slate-500 text-right">
                                          {descVar(v)}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                                <div className="mt-2 text-[11px] text-slate-500 space-y-0.5">
                                  <div>
                                    <span className="font-medium text-slate-600">Fórmula MWh:</span>{" "}
                                    <span className="font-mono">{p.formula_mwh}</span>
                                  </div>
                                  {p.notas && (
                                    <div>
                                      <span className="font-medium text-slate-600">Notas:</span> {p.notas}
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Input({
  label,
  value,
  onChange,
  type = "text",
  placeholder,
  hint,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  hint?: string;
}) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-slate-600">{label}</span>
      {hint && <span className="block text-[10px] text-slate-400">{hint}</span>}
      <input
        type={type}
        step={type === "number" ? "any" : undefined}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 border rounded px-2 py-1.5 w-full text-sm tabular-nums"
      />
    </label>
  );
}
